import type { HiveClient, TeamContext, Conformance } from "@br9704/aethereum-shared";
import type { OutboxEntry, WriteOutbox } from "./write-outbox.js";

export type { HiveClient } from "@br9704/aethereum-shared";

/**
 * HiveClient that talks to the LOCAL room server. Holds only a room-scoped
 * session token — never a Supabase key. The hosted MCP uses `SupabaseHiveStore`
 * (in apps/web) instead. The `HiveClient` interface now lives in shared.
 *
 * v6-S6: an optional `outbox` makes writes durable across an offline stretch.
 * A network failure on a write is persisted, then replayed in order the next
 * time a write reaches the server. The offline write still throws (so the MCP
 * layer degrades exactly as before) — the outbox only adds later replay.
 */
export class HttpHiveClient implements HiveClient {
  private outboxMayHaveItems: boolean;

  constructor(
    private readonly baseUrl: string,
    private readonly sessionToken: string,
    private readonly fetchImpl: typeof fetch = fetch,
    private readonly outbox?: WriteOutbox,
  ) {
    this.outboxMayHaveItems = outbox ? outbox.size() > 0 : false;
  }

  /** Raw POST with no outbox interaction — used by drain replay and by `post`. */
  private async rawPost(path: string, body: Record<string, unknown>): Promise<unknown> {
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: this.sessionToken, ...body }),
    });
    if (!res.ok) throw new Error(`Aethereum ${path} ${res.status}`);
    return res.json();
  }

  /** Replay any queued writes; called opportunistically after a live write. */
  private async drainOutbox(): Promise<void> {
    if (!this.outbox || !this.outboxMayHaveItems) return;
    const send = async (entry: OutboxEntry) => {
      await this.rawPost(entry.path, entry.body);
    };
    // A server rejection ("Aethereum <path> <status>") is permanent: the write
    // was seen and refused, so it must be dropped from the queue rather than
    // block every entry behind it forever. Anything else is a transient offline.
    const isPermanentFailure = (err: unknown) =>
      err instanceof Error && /^Aethereum \S+ \d{3}$/.test(err.message);
    const flushed = await this.outbox.drain(send, isPermanentFailure);
    // Empty now (all delivered or dropped) → clear the flag; else still offline.
    if (flushed >= 0 && this.outbox.size() === 0) this.outboxMayHaveItems = false;
  }

  private async post(path: string, body: Record<string, unknown>): Promise<unknown> {
    let out: unknown;
    try {
      out = await this.rawPost(path, body);
    } catch (err) {
      // Distinguish a network failure (fetch threw — offline) from a server
      // rejection (rawPost throws "Aethereum <path> <status>"). Only the former
      // is worth replaying; a 4xx/5xx was seen by the server.
      const isServerReject = err instanceof Error && /^Aethereum \S+ \d{3}$/.test(err.message);
      if (this.outbox && !isServerReject) {
        this.outbox.enqueue(path, body, Date.now());
        this.outboxMayHaveItems = true;
      }
      throw err;
    }
    // The write landed — a good moment to flush anything stranded while offline.
    await this.drainOutbox();
    return out;
  }

  async shareIntent(text: string): Promise<void> {
    await this.post("/hive/intent", { text });
  }

  async declareContract(
    name: string,
    shape: string,
    dependsOn?: string[],
    stability?: "stable" | "unstable" | "frozen",
    files?: string[],
    conformance?: Conformance,
  ): Promise<{ version: number }> {
    // B6: thread `stability` through — it was silently dropped on the local path,
    // so declare_contract(..., stability) and frozen/unstable never reached teammates.
    // CAP-1: `files` globs ride along for the git-boundary gate.
    // G1: `conformance` (the behavioural spec) rides along too.
    const out = (await this.post("/hive/contracts", { name, shape, dependsOn, stability, files, conformance })) as {
      version: number;
    };
    return { version: out.version };
  }

  // B7: these tools were dead no-ops on a local `aethereum serve` room (no route).
  async setContractStatus(name: string, stability: "stable" | "unstable" | "frozen"): Promise<void> {
    await this.post("/hive/contract-status", { name, stability });
  }

  async setDirective(text: string, targets?: string[]): Promise<void> {
    await this.post("/hive/directive", { text, targets });
  }

  async clearDirective(): Promise<void> {
    await this.post("/hive/directive/clear", {});
  }

  async dependOn(name: string): Promise<void> {
    await this.post("/hive/depend-on", { name });
  }

  async contractHistory(name: string): Promise<Array<{ version: number; shape: string; ts: number }>> {
    const out = (await this.post("/hive/contract-history", { name })) as {
      versions?: Array<{ version: number; shape: string; ts: number }>;
    };
    return out.versions ?? [];
  }

  async setRuleset(text: string): Promise<{ version: number }> {
    const out = (await this.post("/hive/ruleset", { text })) as { version: number };
    return { version: out.version };
  }

  async sendMessage(to: string, text: string): Promise<{ to: string | null; roster: string[] }> {
    const out = (await this.post("/hive/message", { to, text })) as {
      to?: string | null;
      roster?: string[];
    };
    return { to: out.to ?? null, roster: out.roster ?? [] };
  }

  async recordDecision(text: string, tags?: string[]): Promise<{ recordedAt: number }> {
    await this.post("/hive/decision", { text, tags });
    return { recordedAt: Date.now() };
  }

  async sharePlan(steps: Array<{ text: string; status: "todo" | "doing" | "done" }>): Promise<void> {
    await this.post("/hive/plan", { steps });
  }

  async setBrief(text: string): Promise<void> {
    await this.post("/hive/brief", { text });
  }

  async recordVerification(input: {
    status: "pass" | "fail" | "partial";
    summary: string;
    target?: string;
    score?: number;
    evidence?: string;
  }): Promise<void> {
    await this.post("/hive/verification", input);
  }

  async setGoal(input: {
    text: string;
    maxIterations?: number;
    budgetTokens?: number;
    stopCondition?: string;
  }): Promise<{ goalId: string }> {
    const out = (await this.post("/hive/goal", input)) as { goalId: string };
    return { goalId: out.goalId };
  }

  async updateGoal(input: {
    goalId: string;
    status?: "active" | "blocked" | "done" | "abandoned";
    iteration?: number;
    spentTokens?: number;
  }): Promise<void> {
    await this.post("/hive/goal/update", input);
  }

  async searchMemory(
    query: string,
    limit?: number,
  ): Promise<Array<{ type: string; text: string; by?: string; ts: number; score: number }>> {
    const params = new URLSearchParams({ token: this.sessionToken, q: query });
    if (limit) params.set("limit", String(limit));
    const res = await this.fetchImpl(`${this.baseUrl}/hive/search?${params.toString()}`);
    if (!res.ok) throw new Error(`Aethereum /hive/search ${res.status}`);
    const out = (await res.json()) as {
      results?: Array<{ type: string; text: string; by?: string; ts: number; score: number }>;
    };
    return out.results ?? [];
  }

  async recordClaim(area: string): Promise<void> {
    await this.post("/hive/claim", { area });
  }

  async releaseClaim(area: string): Promise<void> {
    await this.post("/hive/release", { area });
  }

  async proposeContract(
    name: string,
    newShape: string,
    rationale?: string,
    dependsOn?: string[],
  ): Promise<{ proposalId: string }> {
    const out = (await this.post("/hive/propose", { name, newShape, rationale, dependsOn })) as {
      proposalId: string;
    };
    return { proposalId: out.proposalId };
  }

  async respondToProposal(
    proposalId: string,
    verdict: "accepted" | "changes_requested" | "rejected",
    reason?: string,
    counterShape?: string,
  ): Promise<void> {
    await this.post("/hive/respond", { proposalId, verdict, reason, counterShape });
  }

  async finalizeProposal(proposalId: string): Promise<{ version: number }> {
    const out = (await this.post("/hive/finalize", { proposalId })) as { version: number };
    return { version: out.version };
  }

  async getTeamContext(): Promise<TeamContext> {
    const res = await this.fetchImpl(
      `${this.baseUrl}/hive/context?token=${encodeURIComponent(this.sessionToken)}`,
    );
    if (!res.ok) throw new Error(`Aethereum /hive/context ${res.status}`);
    return (await res.json()) as TeamContext;
  }

  /** Read-only snapshot — never advances the alert cursor (MCP resources). */
  async peekTeamContext(): Promise<TeamContext> {
    const res = await this.fetchImpl(
      `${this.baseUrl}/hive/context?token=${encodeURIComponent(this.sessionToken)}&peek=1`,
    );
    if (!res.ok) throw new Error(`Aethereum /hive/context ${res.status}`);
    return (await res.json()) as TeamContext;
  }

  /**
   * Cheap NON-consuming read for the post-write awareness footer: alerts +
   * presence without advancing the cursor, so a write between two explicit
   * get_team_context calls can't steal the alert (bug B4). Uses the same
   * member-scoped peek as `peekTeamContext`.
   */
  async awarenessSnapshot(): Promise<{ alerts: TeamContext["alerts"]; presence: TeamContext["presence"] }> {
    const ctx = await this.peekTeamContext();
    return { alerts: ctx.alerts, presence: ctx.presence };
  }
}
